import React, { useMemo, useState } from "react";
import { Organisation, PlanRun } from "../types";
import {
  Calendar,
  ChevronRight,
  Clock,
  History,
  Search,
  Trash2,
} from "./Icons";
import { formatRunTime } from "../shared/date";
import { useNavigate } from "react-router-dom";
import { ConfirmationModal } from "./Modals";
import { UpgradePrompt } from "./UpgradePrompt";

interface RunsListProps {
  runs: PlanRun[];
  currentOrg: Organisation;
  onDeleteRun: (id: string) => void;
  onRefresh?: () => void;
}

const RunsList: React.FC<RunsListProps> = ({
  runs,
  currentOrg,
  onDeleteRun,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteRunId, setDeleteRunId] = useState<string | null>(null);
  const isMaintainer = currentOrg.role === "MAINTAINER";

  const filteredRuns = useMemo(() => {
    return runs
      .filter((r) => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => {
        // Sort by start time descending (newest first)
        const timeA =
          a.status === "SCHEDULED" ? a.scheduledStartTime || 0 : a.startTime;
        const timeB =
          b.status === "SCHEDULED" ? b.scheduledStartTime || 0 : b.startTime;
        return timeB - timeA;
      });
  }, [runs, searchTerm]);

  const scheduledRuns = filteredRuns.filter((r) => r.status === "SCHEDULED");
  const pastRuns = filteredRuns.filter((r) => r.status !== "SCHEDULED");

  const handleResumeRun = (runId: string) => {
    navigate(`/${currentOrg.slug}/runs/${runId}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Runs
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            View all previous and scheduled plan executions.
          </p>
        </div>
        <div className="flex items-center gap-3 w-full lg:w-auto">
          <div className="relative flex-1 lg:flex-none lg:w-64">
            <Search
              className="absolute left-3 top-2.5 text-slate-400"
              size={16}
            />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search runs..."
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
            />
          </div>
        </div>
      </div>

      {/* Subscription Limit Up-sell */}
      {currentOrg.subscriptionStatus === "FREE" && (
        <div className="mb-8">
          <UpgradePrompt
            currentOrg={currentOrg}
            title="Extended History"
            message="Free workspaces only show the last 7 days of run history. Upgrade to Team or Enterprise for full access to your workspace's entire history."
            onUpgrade={() => navigate(`/${currentOrg.slug}/workspace/billing`)}
          />
        </div>
      )}

      <div className="space-y-8">
        {/* Scheduled Runs Section */}
        {scheduledRuns.length > 0 && (
          <section>
            <div className="flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
              <Calendar size={16} /> Scheduled Runs
            </div>
            <div
              data-testid="scheduled-runs-list"
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
            >
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {scheduledRuns.map((run) => (
                  <RunListItem
                    key={run.id}
                    run={run}
                    isMaintainer={isMaintainer}
                    onClick={() => handleResumeRun(run.id)}
                    onDelete={() => setDeleteRunId(run.id)}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Past Runs Section */}
        <section>
          <div className="flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
            <History size={16} /> Past Runs
          </div>
          <div
            data-testid="past-runs-list"
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
          >
            {pastRuns.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                  <History size={24} />
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                  No run history found
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mt-1">
                  {searchTerm
                    ? `No runs match "${searchTerm}"`
                    : "No plans have been executed yet."}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {pastRuns.map((run) => (
                  <RunListItem
                    key={run.id}
                    run={run}
                    isMaintainer={isMaintainer}
                    onClick={() => handleResumeRun(run.id)}
                    onDelete={() => setDeleteRunId(run.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <ConfirmationModal
        isOpen={!!deleteRunId}
        onClose={() => setDeleteRunId(null)}
        onConfirm={() => {
          if (deleteRunId) {
            onDeleteRun(deleteRunId);
            setDeleteRunId(null);
          }
        }}
        title="Delete Run"
        message="Are you sure you want to delete this run history? This cannot be undone."
        confirmText="Delete Run"
        confirmVariant="danger"
      />
    </div>
  );
};

interface RunListItemProps {
  run: PlanRun;
  isMaintainer: boolean;
  onClick: () => void;
  onDelete: () => void;
}

const RunListItem: React.FC<RunListItemProps> = ({
  run,
  isMaintainer,
  onClick,
  onDelete,
}) => {
  return (
    <div
      onClick={onClick}
      data-testid="run-list-item"
      className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition cursor-pointer group relative flex items-center justify-between"
    >
      <div className="flex items-center gap-4 overflow-hidden">
        <StatusIcon status={run.status} />
        <div className="min-w-0">
          <div className="font-medium text-slate-900 dark:text-white truncate">
            {run.name}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            <span
              className="flex items-center gap-1"
              title={formatRunTime(run.status, run.startTime, run.scheduledStartTime).full}
            >
              <Clock size={12} />
              {formatRunTime(run.status, run.startTime, run.scheduledStartTime).relative}
            </span>
            {run.status === "SCHEDULED" && (
              <span className="text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/20 px-1.5 py-0.5 rounded">
                Scheduled
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 pl-4">
        <StatusBadge status={run.status} />
        {isMaintainer && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            title="Delete Run"
          >
            <Trash2 size={14} />
          </button>
        )}
        <ChevronRight
          size={16}
          className="text-slate-300 dark:text-slate-600"
        />
      </div>
    </div>
  );
};

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case "COMPLETED":
      return (
        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 flex-shrink-0">
          <History size={16} />
        </div>
      );
    case "RUNNING":
      return (
        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0 animate-pulse">
          <History size={16} />
        </div>
      );
    case "FAILED":
      return (
        <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 flex-shrink-0">
          <History size={16} />
        </div>
      );
    default:
      return (
        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 flex-shrink-0">
          <Calendar size={16} />
        </div>
      );
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  let classes = "";
  switch (status) {
    case "COMPLETED":
      classes =
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300";
      break;
    case "RUNNING":
      classes =
        "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 animate-pulse";
      break;
    case "SCHEDULED":
      classes =
        "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300";
      break;
    case "FAILED":
      classes = "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300";
      break;
    default:
      classes =
        "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400";
  }

  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${classes}`}
    >
      {status}
    </span>
  );
};

export default RunsList;
